import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputLabelClasses } from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import { Messages } from 'common/components/Elements/ExpandableMessages/ExpandableMessages';
import InputAdormentLoading from 'common/components/Elements/Form/InputAdormentLoading/InputAdormentLoading';
import InputDynamicLabel from 'common/components/Elements/Form/InputDynamicLabel/InputDynamicLabel';
import InputText from 'common/components/Elements/Form/InputText/InputText';
import type { TooltipHelp } from 'common/components/Elements/Form/InputTooltipHelp/InputTooltipHelp';
import InputTooltipHelp from 'common/components/Elements/Form/InputTooltipHelp/InputTooltipHelp';
import { generateRandomString } from 'common/utilities/core/string';
import useMenuPosition from 'common/utilities/hooks/useMenuPosition/useMenuPosition';
import { queryRegExp } from 'common/utilities/regexp/regexp';
import type * as I from 'core/api/coreApiClient';
import { inputStyles, inTableInputMarginTop, inTableInputMinWidth } from 'core/utilities/styles/input.styles';
import { sxClassName, sxSubClassName } from 'core/utilities/theme';
import debounce from 'debounce';
import I18n from 'i18n/en-US';
import React, {
  useCallback, useRef, useState,
} from 'react';

import AutosuggestPopper from './AutosuggestPopper';
import type { OnChangeValue,
  OptionType, OptionTypeData, SelectOptionTypeRange} from './common';
import {
classes, filterOutOptions,   handleChange, isOptionValue, markOptionsWithinValuesAsSelected, renderOption,
useDropdownScrollPosition, } from './common';
import ListboxComponent from './ListboxComponent';

const getMoreOption = (): OptionType => ({
  value: 'moreOption',
  label: I18n.Elements.Form.InputAdvancedSelect.more,
  isDisabled: true,
});

export const ShowMoreOptionValue = 'showMoreOption';

const getShowMoreOption = (optionsHidden: number, optionsForNextPage: number): OptionType => ({
  value: ShowMoreOptionValue,
  label: I18n.Elements.Form.InputAdvancedSelect.showMore(optionsHidden, optionsForNextPage),
});

const formatOptions = (
  values: OptionType[],
  options: OptionType[],
  optionsShown: number | undefined,
  optionsPerPage: number | undefined,
  isMulti: boolean | undefined,
): OptionType[] => {
  const optionsMarkedAsSelected = markOptionsWithinValuesAsSelected(isMulti, options, values);

  if (optionsShown !== undefined && optionsMarkedAsSelected.length > optionsShown) {
    const newOptions = [...optionsMarkedAsSelected.slice(0, optionsShown)];

    if (optionsPerPage !== undefined) {
      const optionsHidden = optionsMarkedAsSelected.length - optionsShown;
      const optionsForNextPage = optionsHidden > optionsPerPage ? optionsPerPage : optionsHidden;
      newOptions.push(getShowMoreOption(optionsHidden, optionsForNextPage));
    }
    newOptions.push(getMoreOption());
    return newOptions;
  }

  return optionsMarkedAsSelected;
};

export interface ComponentProps {
  label?: string;
  title?: string;
  values: OptionType[];
  disabled?: boolean;
  onChange: OnChangeValue;
  outlined?: boolean;
  e2eId?: string;
  isMulti?: boolean;
  options: OptionType[];
  optionsShown?: number;
  optionsPerPage?: number;
  longFormStyle?: boolean;
  placeholder?: string;
  maxWidth?: string;
  clearable?: boolean;
  required?: boolean;
  errorMsg?: I.Message[];
  sideLabel?: boolean;
  modalLabel?: boolean;
  ariaLabel?: string;
  tooltipHelp?: TooltipHelp;
  loading?: boolean;
  helpInfo?: I.Denotation;
  isMultiLine?: boolean;
  inTable?: boolean;
}

interface OptionsState {
  options: OptionType[];
  loading: boolean;
  inputText: string;
  inputTextEventType: string;
  open: boolean;
}

function InputAdvancedSelect({
  label, values, disabled, onChange, outlined, isMulti, placeholder,
  e2eId, options, optionsShown, optionsPerPage,
  longFormStyle, clearable, required, errorMsg, maxWidth, sideLabel, modalLabel, ariaLabel,
  tooltipHelp, loading, title, helpInfo, isMultiLine, inTable,
}: ComponentProps) {
  const styles = inputStyles({
    longFormStyle,
    sideLabel,
    modalLabel,
    errorMsg,
    inTable,
    minWidth: inTableInputMinWidth.InputAdvancedSelect,
    marginTop: inTableInputMarginTop.InputAdvancedSelect,
  });

  const [id] = useState(() => generateRandomString());

  const lastSelectedValue = useRef<OptionType | undefined>();
  const lastUnselectedValue = useRef<OptionType | undefined>();

  const { dropdownScrollPosition, storeDropdownScrollPosition } = useDropdownScrollPosition();

  const getFormattedOptions = React.useCallback((opts: OptionType[], vs: OptionType[]) => (
    formatOptions(vs, opts, optionsShown, optionsPerPage, isMulti)
  ), [isMulti, optionsPerPage, optionsShown]);

  const [state, setState] = useState<OptionsState>({
    options: getFormattedOptions(options, values),
    loading: false,
    inputText: values && !isMulti ? values[0]?.label : '',
    inputTextEventType: '',
    open: false,
  });

  const clearState = useCallback(() => {
    storeDropdownScrollPosition();

    setState({
      options: getFormattedOptions(options, values),
      loading: false,
      inputText: '',
      inputTextEventType: '',
      open: false,
    });

    lastSelectedValue.current = undefined;
  }, [getFormattedOptions, options, storeDropdownScrollPosition, values]);

  const startLoading = () => {
    setState((prevState) => ({
      ...prevState, options: [], loading: true, open: true,
    }));
  };

  const fetchOptions = (inputText: string, vs: OptionType[]) => {
    const filteredOptions = options.filter((o) => queryRegExp(inputText).test(o.label));
    const filteredOptionsWithValues = [...filteredOptions];
    vs.forEach((v) => {
      if (!filteredOptionsWithValues.some((o) => isOptionValue(o, v))) {
        filteredOptionsWithValues.push({ ...v, data: { toFilterOut: true } as OptionTypeData });
      }
    });
    setState((prevState) => ({
      ...prevState,
      options: getFormattedOptions(filteredOptionsWithValues, vs),
      loading: false,
    }));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchOptions = React.useCallback(
    debounce((inputValue: string, vs: OptionType[]) => {
      fetchOptions(inputValue, vs);
    }, 400),
    [],
  );

  React.useEffect(() => {
    if (state.inputTextEventType === 'change') {
      startLoading();
      debouncedFetchOptions(state.inputText, values);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFetchOptions, state.inputText, state.inputTextEventType]);

  const { wrapperRef, menuPosition } = useMenuPosition();

  const value = isMulti || !values ? values : values[0];

  let inputEndAdornment = (
    <InputAdornment position="end">
      {clearable && (
        <Box mr={-1}>
          <IconButton
            onClick={() => onChange()}
            data-testid="reset"
            size="small"
          >
            <ClearIcon style={{ fontSize: '16px' }} />
          </IconButton>
        </Box>
      )}
      {tooltipHelp && <Box mr={-1}><InputTooltipHelp tooltipHelp={tooltipHelp} /></Box>}
      <IconButton
        onClick={() => {
          // Do nothing, click through will trigger the input
        }}
        size="small"
        aria-hidden="true"
        role="presentation"
        tabIndex={-1}
        disabled={disabled}
      >
        <KeyboardArrowDownIcon color={disabled ? 'disabled' : 'action'} />
      </IconButton>
    </InputAdornment>
  );

  if (loading) {
    inputEndAdornment = <InputAdormentLoading />;
  }

  return (
    <div ref={wrapperRef} data-testid="InputAdvancedSelect">
      <Autocomplete
        id={id}
        multiple={isMulti}
        value={value || null}
        options={state.options}
        open={state.open}
        // Disable filter as the final list is formatted already
        filterOptions={filterOutOptions}
        isOptionEqualToValue={(o, v) => isOptionValue(o, v)}
        getOptionDisabled={(option) => (
          option && typeof option !== 'string' && option.isDisabled !== undefined ? option.isDisabled : false
        )}
        size="small"
        openOnFocus
        blurOnSelect={!isMulti}
        onChange={(event, newValue, reason, details) => {
          let selectRange: SelectOptionTypeRange | undefined;

          if (
            isMulti
            && (event as unknown as KeyboardEvent).shiftKey
            && reason === 'selectOption'
            && lastSelectedValue.current
            && details?.option
          ) {
            selectRange = { from: lastSelectedValue.current, to: details?.option, action: reason };
          }

          if (
            isMulti
            && (event as unknown as KeyboardEvent).shiftKey
            && reason === 'removeOption'
            && lastUnselectedValue.current
            && details?.option
          ) {
            selectRange = { from: lastUnselectedValue.current, to: details?.option, action: reason };
          }

          lastSelectedValue.current = reason === 'selectOption' ? details?.option : undefined;
          lastUnselectedValue.current = reason === 'removeOption' ? details?.option : undefined;

          storeDropdownScrollPosition();

          handleChange(onChange, newValue, filterOutOptions(state.options), selectRange);
        }}
        onInputChange={(event, newInputText) => {
          if (event && (event as unknown as KeyboardEvent).key !== 'Enter') {
            setState((prevState) => ({ ...prevState, inputText: newInputText, inputTextEventType: event.type }));
          }
        }}
        disableClearable={!clearable}
        // eslint-disable-next-line react/no-unstable-nested-components, react/display-name
        ListboxComponent={React.forwardRef<
          HTMLDivElement,
          React.HTMLAttributes<HTMLElement>
        >((props, ref) => (
          <ListboxComponent
            forwardedRef={ref}
            forwardedProps={props}
            scrollPosition={dropdownScrollPosition}
          />
        ))}
        ListboxProps={{
          style: {
            maxHeight: menuPosition.maxHeight,
          },
        }}
        // eslint-disable-next-line react/no-unstable-nested-components
        PopperComponent={((props) => (
          <AutosuggestPopper
             
            {...props}
            options={state.options}
            placement={menuPosition.menuPlacement === 'top' && state.options.length > 1 ? 'top-start' : 'bottom-start'}
          />
        ))}
        fullWidth
        popupIcon={<KeyboardArrowDownIcon color="action" />}
        sx={{
          [sxSubClassName(autocompleteClasses.noOptions)]: classes.noOptions,
          [sxSubClassName(autocompleteClasses.loading)]: classes.loading,
          [sxSubClassName(autocompleteClasses.option)]: classes.option,
          [sxSubClassName(autocompleteClasses.inputRoot)]: classes.inputRoot,
        }}
        disabled={disabled}
        renderOption={(props, option) => renderOption(isMulti, props, option)}
        renderTags={isMultiLine ? () => null : undefined}
        renderInput={(params) => (
          <InputDynamicLabel
            sideLabel={sideLabel}
            htmlFor={id}
            disabled={disabled}
            errorMsg={errorMsg}
            label={label || ''}
            required={required}
            title={title}
          >
            <TextField
               
              {...params}
              sx={styles.root}
              placeholder={placeholder}
              variant={outlined ? 'outlined' : 'standard'}
              label={sideLabel ? undefined : label}
              title={title}
              disabled={disabled}
              required={required}
              data-e2e-form-advanced-selection={e2eId}
              data-e2e-form-advanced-selection-loading={state.loading}
              fullWidth
              margin="dense"
              style={{ maxWidth }}
              error={errorMsg && errorMsg.length > 0}
              helperText={<Messages messages={errorMsg} denotation={helpInfo} />}
              InputLabelProps={{
                htmlFor: id,
                sx: {
                  [sxClassName(inputLabelClasses.root)]: styles.labelRoot,
                  [sxClassName(inputLabelClasses.disabled)]: styles.labelDisabled,
                  [sxClassName(inputLabelClasses.shrink)]: styles.labelShrink,
                },
              }}
              InputProps={{
                ...params.InputProps,
                'aria-label': label ? undefined : ariaLabel,
                sx: {
                  [sxClassName(inputBaseClasses.root)]: styles.inputRoot,
                },
                endAdornment: inputEndAdornment,
                onKeyDown: (e) => {
                  if (e.code === 'Escape') clearState();
                },
                onFocus: () => {
                  setState((prevState) => ({ ...prevState, open: true }));
                },
                onBlur: () => {
                  clearState();
                },
              }}
            />
          </InputDynamicLabel>
        )}
      />

      {isMultiLine && isMulti && Array.isArray(value) && (
        <InputText
          value={value.map((v) => v.label).join('\n')}
          onChange={(newValue) => {
            const newOptions: OptionType[] = newValue.split('\n').map((v) => ({
              label: v,
              value: v,
            }));
            handleChange(onChange, newOptions, []);
          }}
          multiline
          fullWidth
          textSmall
          longFormStyle={longFormStyle}
          sideLabel={sideLabel}
          e2eId={e2eId}
          connectingLine
        />
      )}
    </div>
  );
}

export default InputAdvancedSelect;
